import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { __ } from 'first-intl';
import { FormData } from '@hugendubel/morfi';
import { Button, FormButtons, FormRadioGroup, Toaster, TrackingForm } from '@hugendubel/react-basic';
import { DomUtil } from '@hugendubel/utils';
import { TRACK_ID } from '@hugendubel/tracking_client';
import { AddAddressForm } from './AddAddressForm/AddAddressForm';
import { AppRoutes } from '../../appRoutes';
import { WsBaseAddress } from '../../../ws/result/WsBaseAddress';
import { Store } from '../../../store';
import { LegalInfo } from '../order/LegalInfo';
import { OrderService } from '../../../services/order-service';
import { DeliveryService } from '../../../services/delivery-service';
import { AddressView } from './AddressView';
import { externalHistory } from '../../hooks/useExposeNavigate';
import { useParams } from 'react-router-dom';

export const onAbort = () => externalHistory.navigate(AppRoutes.HOME);

export const useArticleId = (articleIdString?: string): number | undefined => {
    const articleId = useMemo((): number | undefined => {
        const parsedArticleId = Number(articleIdString);
        if (parsedArticleId && !isNaN(parsedArticleId)) return parsedArticleId;
    }, [articleIdString]);

    // leave the page if the articleId could not be obtained from the pathname
    useEffect(() => {
        if (!articleId) externalHistory.navigate(AppRoutes.HOME);
    }, [articleId]);

    return articleId;
};

const useAddressAutoSelect = (
    addresses: WsBaseAddress[],
    setData: (data: FormData<DeliverySelectionValues>) => void
): void => {
    const deliveryAddressesRef = useRef<WsBaseAddress[]>(addresses);
    deliveryAddressesRef.current = addresses;

    useEffect(
        () => () => {
            const nextAddresses = deliveryAddressesRef.current;
            const addressCount = nextAddresses.length;

            if (addressCount === addresses.length + 1) {
                // addresses increased by one, so we want to select the last added
                setData({ values: { address: nextAddresses[addressCount - 1] }, errors: {} });
            } else if (addressCount === 1) {
                setData({ values: { address: nextAddresses[0] }, errors: {} });
            }
        },
        [addresses.length, setData]
    );
};

type DeliverySelectionValues = { address: WsBaseAddress | undefined };
const initialValues = { address: undefined };

const { Form, Fields } = TrackingForm.create<DeliverySelectionValues>(initialValues, TRACK_ID('2019-09-03 03:08:35'), {
    allowMultiSubmit: true,
});

export type DeliverySelectionPageStoreProps = { addresses: WsBaseAddress[] };
export type DeliverySelectionPageProps = DeliverySelectionPageStoreProps;

export const UnconnectedDeliverySelectionPage: React.FC<DeliverySelectionPageProps> = ({ addresses }) => {
    const [showAddAddress, setShowAddAddress] = useState(false);
    // please notice that the configured autoFocus on the input fields of the form directly
    // scrolls the user to that input. Therefore we do not need any further scroll action
    const openAddAddress = useCallback(() => setShowAddAddress(true), []);
    const closeAddAddress = useCallback(() => {
        setShowAddAddress(false);
        DomUtil.scrollToClass('address-options');
    }, []);

    const [data, setData] = useState<FormData<DeliverySelectionValues>>({ values: initialValues, errors: {} });
    const { articleId } = useParams();
    const parsedArticleId = useArticleId(articleId);
    useAddressAutoSelect(addresses, setData);

    useEffect(() => {
        // TODO(VL): Load only once per application life time AND select the only addresses
        //           OR persist the selection on application life time OR even longer
        DeliveryService.loadAddresses();
    }, []);

    const onSubmit = useCallback(
        ({ address }: DeliverySelectionValues) => {
            if (!address || !address.isGermanAddress()) {
                return Toaster.open('page.DeliveryAddressSelection.noAbroadAddress');
            } else {
                return OrderService.delivery(parsedArticleId as any, address.encodedId);
            }
        },
        [parsedArticleId]
    );

    const deliveryOptions = useMemo(
        () => addresses.map(a => ({ value: a, node: <AddressView address={a} key={a.encodedId} /> })),
        [addresses]
    );

    return (
        <div className="container-fluid">
            <Form data={data} onSubmit={onSubmit} validation={{}} onChange={setData}>
                <div className="row">
                    <div className="col-sm-12">
                        <h1>{__('page.DeliveryAddressSelection.title')}</h1>
                        <p>
                            <strong>{__('page.DeliveryAddressSelection.header.delivery')}</strong>
                        </p>
                        <p>{__('addresses.info.noForeignAddresses')}</p>
                    </div>
                    <div className="col-sm-12 address-options">
                        <FormRadioGroup
                            Field={Fields.address}
                            trackId={TRACK_ID('2019-09-03 03:50:32')}
                            className="radio-group radio-option-group option-row wrap customer-data-group cols-3"
                            optionClass="radio-right"
                            options={deliveryOptions}
                        />
                        <Button
                            className="btn btn-default icon-plus"
                            label="form.addAddress.createNew"
                            type="button"
                            onClick={openAddAddress}
                        />
                    </div>
                    <LegalInfo />
                </div>
                <FormButtons
                    submitLabel="page.DeliveryAddressSelection.button.checkout"
                    loading={data.submitting}
                    disabled={!data.values.address}
                    onAbort={onAbort}
                    abortLabel="page.DeliveryAddressSelection.button.abort"
                />
            </Form>
            {showAddAddress && <AddAddressForm onClose={closeAddAddress} />}
        </div>
    );
};

// we need this constant too avoid building inline array which would fail shallow comparison checks
const NoAddresses: WsBaseAddress[] = [];

export const DeliverySelectionPage = Store.wire<DeliverySelectionPageStoreProps, Record<string, unknown>>(
    UnconnectedDeliverySelectionPage,
    ({ deliveryAddresses }) => ({ addresses: deliveryAddresses ? deliveryAddresses.items : NoAddresses })
);
